import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { ArrowLeft, Star, User, Phone, Mail, Calendar, MessageSquare } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { useReview } from '../hooks';
import { RatingStars } from '../components';
import type { ReviewUser } from '../types';

export function ReviewDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { t } = useTranslation();
  const { data: review, isLoading, isError, error } = useReview(id!);

  const formatDate = (dateStr?: string) =>
    dateStr ? format(new Date(dateStr), 'yyyy-MM-dd HH:mm') : '-';

  const renderUserCard = (user: ReviewUser, title: string) => (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <User className="h-5 w-5" />
          {title}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <p className="text-sm text-muted-foreground">{t('id')}</p>
            <p className="font-medium">{user.id}</p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">{t('name')}</p>
            <Link
              to={`/app/users/${user.id}`}
              className="font-medium text-blue-700 hover:underline"
            >
              {user.name}
            </Link>
          </div>
          <div>
            <p className="text-sm text-muted-foreground flex items-center gap-1">
              <Phone className="h-3 w-3" /> {t('phone')}
            </p>
            <p className="font-medium">{user.phone || '-'}</p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground flex items-center gap-1">
              <Mail className="h-3 w-3" /> {t('email')}
            </p>
            <p className="font-medium">{user.email || '-'}</p>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">{t('status.status')}</p>
            <Badge variant={user.status === 'ACTIVE' ? 'default' : 'destructive'}>
              {user.status || '-'}
            </Badge>
          </div>
          <div>
            <p className="text-sm text-muted-foreground">{t('roleId')}</p>
            <p className="font-medium">{user.roleId || '-'}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  if (isLoading) {
    return (
      <div className="p-4 space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (isError || !review) {
    return (
      <div className="p-4">
        <p className="text-red-500">{error?.message || 'Review not found'}</p>
        <Link to="/app/reviews">
          <Button variant="outline" className="mt-4">
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t('backToList')}
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="p-4 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Link to="/app/reviews">
            <Button variant="ghost" size="sm">
              <ArrowLeft className="mr-2 h-4 w-4" />
              {t('back')}
            </Button>
          </Link>
          <div className="flex items-center gap-2">
            <Star className="h-6 w-6" />
            <h1 className="text-2xl font-bold">{t('reviewDetails')}</h1>
          </div>
        </div>
      </div>

      {/* Review Information */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MessageSquare className="h-5 w-5" />
            {t('reviewInformation')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <p className="text-sm text-muted-foreground">{t('id')}</p>
              <p className="font-medium">{review.id}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">{t('rating')}</p>
              <RatingStars rating={review.rating} />
            </div>
            <div className="col-span-2">
              <p className="text-sm text-muted-foreground">{t('comment')}</p>
              <p className="font-medium">{review.comment || '-'}</p>
            </div>
            {review.kpiId && (
              <div>
                <p className="text-sm text-muted-foreground">{t('kpiId')}</p>
                <p className="font-medium">{review.kpiId}</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Timestamps */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            {t('timestamps')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-sm text-muted-foreground">{t('createdAt')}</p>
              <p className="font-medium">{formatDate(review.createdAt)}</p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground">{t('updatedAt')}</p>
              <p className="font-medium">{formatDate(review.updatedAt)}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* User Information */}
      {review.user && renderUserCard(review.user, t('userInformation'))}

      {/* Created By Information */}
      {review.createdBy && renderUserCard(review.createdBy, t('createdByInformation'))}
    </div>
  );
}
